export enum Status {
  DONE = 'done',
  WAITING = 'waiting',
  TODO = 'todo',
  NONE = 'none',
}

export enum Billing {
  NONE = 'none',
  NEW = 'new',
  MAINTENANCE = 'maintenance',
  DISPOSED = 'disposed',
}

export enum ConditionValue {
  OK = 'ok',
  NOT_OK = 'notok',
  NEW = 'new',
}

export enum PressureClass {
  BOOST = 'boost',
  PERMANENT = 'permanent',
}

export enum WorkerRole {
  SUPERADMIN = 'superadmin',
  ADMIN = 'admin',
  STAFF = 'staff',
  TECHNICIAN = 'technician',
}

export enum SparePartType {
  EXTINGUISHER = 'extinguisher',
  HOSE = 'hose',
}

export enum VisitStatus {
  PLANNED = 'planned',
  CONFIRMED = 'confirmed',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export interface Visit {
  id: string;
  customerId: string;
  date: string;
  worker?: string;
  status: VisitStatus;
  notes?: string;
}

export interface Address {
  company?: string;
  addition?: string;
  street?: string;
  zip?: string;
  city?: string;
  country?: string;
  phone?: string;
  email?: string;
  attn?: string;
}

export enum ContactRole {
  JANITOR = 'janitor',
  JANITOR_2 = 'janitor2',
  MANAGEMENT = 'management',
}

export interface Contact {
  role: ContactRole;
  name?: string;
  phone?: string;
}

export interface Extinguisher {
  id: string;
  section?: string;
  floor?: string;
  location?: string;
  mfr?: string;
  model?: string;
  content?: string;
  type?: string;
  deviceNumber?: string;
  year?: number;
  lastInspection?: string;
  nextInspection?: string;
  refilled?: string;
  billing?: Billing;
  spareParts?: string[];
  notes?: string;
  documentationPdf?: string;
  maintainedThisCycle?: boolean;
}

export interface HoseCondition {
  fireCock: ConditionValue;
  seals: ConditionValue;
  hose: ConditionValue;
  nozzle: ConditionValue;
  axle: ConditionValue;
  swivelArm: ConditionValue;
  doorLock: ConditionValue;
  water: ConditionValue;
}

export interface Hose {
  id: string;
  location?: string;
  mfr?: string;
  model?: string;
  year?: number;
  length?: number;
  diameter?: string;
  connectionDiameter?: string;
  connection?: string;
  condition: HoseCondition;
  lastService?: string;
  nextService?: string;
  restingPressure?: string;
  flowPressure?: string;
  flowRate?: string;
  pressureTestDate?: string;
  spareParts?: string[];
  status: Status;
}

export interface Customer {
  id: string;
  name: string;
  objectNr?: string;
  status: Status;
  worker?: string;
  createdBy?: string;
  nextInspection?: string;
  lastInspection?: string;
  created?: string;
  billingAddress?: Address;
  objectAddress?: Address;
  ownerAddress?: Address;
  contact?: Address;
  contacts?: Contact[];
  lat?: number;
  lng?: number;
  notes?: string;
  serviceContract?: boolean;
  serviceContractPdf?: string;
  linkedCustomerIds?: string[];
  extinguishers: Extinguisher[];
  hoses: Hose[];
  archives: Archive[];
  deviceCounts?: Record<string, number>;
}

export interface ArchiveDevice {
  deviceNumber?: string;
  floor?: string;
  location?: string;
  mfr?: string;
  model?: string;
  content?: string;
  type?: string;
  year?: number;
  lastInspection?: string;
  nextInspection?: string;
  refilled?: string;
  spareParts?: string[];
}

export interface Archive {
  id: string;
  date: string;
  deviceCount: number;
  technician: string;
  devices?: ArchiveDevice[];
}

export interface Manufacturer {
  id: string;
  name: string;
  models: Model[];
}

export interface Model {
  id: string;
  mfrId: string;
  name: string;
  content: string;
  deviceType: string;
  agentType: string;
}

export interface DeviceType {
  id: string;
  nameDe: string;
  nameEn: string;
  pressureClass: PressureClass;
  ageLimit: number;
}

export interface SparePart {
  articleNr: string;
  name: string;
  type: SparePartType;
  price: number;
  models?: string[];
}

export interface ServicePrice {
  articleNr: string;
  description: string;
  deviceType: string;
  qtyRange: string;
  price: number;
}

export interface Worker {
  id: string;
  name: string;
  role: WorkerRole;
  active: boolean;
}

export interface HoseConfig {
  hoseDiameters: string[];
  connectionDiameters: string[];
  hoseModels: string[];
}

export interface Settings {
  country: string;
  companyName: string;
  vatNumber: string;
  street: string;
  zip: string;
  city: string;
  phone: string;
  email: string;
  vatPercent: number;
  currency: string;
  rounding: string;
  paymentTerms: number;
  inspectionInterval: number;
  boostAgeLimit: number;
  permanentAgeLimit: number;
  hoseTestInterval: number;
  travelSurcharge: number;
  iban: string;
}
